import React, { type ReactNode } from 'react'
import Dotdotdot from 'react-dotdotdot'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { createSearchParams, Link } from 'react-router-dom'
import KibTag from '@chewy/kib-tag-react'
import { Grid, Stack, TableCell } from '@mui/material'
import classNames from 'classnames'
import { PermissionArea } from '@pbt/pbt-ui-components'
import HtmlContentRenderer from '@pbt/pbt-ui-components/src/components/HtmlContentRenderer'

import { BaseRoute } from '~/constants/routes'
import { getCRUDByArea } from '~/store/reducers/auth'
import { replaceHtmlPairedTagWithAnother } from '~/utils'

import styles from './SubjectCell.module.scss'

const SubjectCell = ({
  subject,
  message,
  isDraft = false,
  isUnread = false,
  conversationId,
  messageId,
  noBottomBorder,
}: {
  conversationId: string
  isDraft?: boolean
  isUnread?: boolean
  message: string
  messageId?: string
  noBottomBorder?: boolean
  subject: string | ReactNode
}) => {
  const { t } = useTranslation(['Common'])
  const { read: communicationReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.COMMUNICATION),
  )
  const messagePreview = replaceHtmlPairedTagWithAnother(message, 'pre', 'div')
  const searchParams = messageId
    ? `?${createSearchParams({ message: messageId })}`
    : ''
  const conversationLink = `${BaseRoute.COMMUNICATIONS}/${conversationId}${searchParams}`
  const SubjectComponent = (
    <Dotdotdot
      clamp={1}
      className={classNames(styles.subject, {
        [styles.unread]: isUnread,
      })}
    >
      {subject}
    </Dotdotdot>
  )

  return (
    <TableCell
      className={classNames({ [styles.noBottomBorder]: noBottomBorder })}
    >
      <Grid>
        {communicationReadPermissions ? (
          <Link
            className={classNames(styles.subject, {
              [styles.underlineLink]: !messageId,
              [styles.messageSubject]: Boolean(messageId),
            })}
            to={conversationLink}
          >
            {SubjectComponent}
          </Link>
        ) : (
          SubjectComponent
        )}
        <Stack
          alignItems="center"
          data-testid="message-preview"
          direction="row"
          gap={0.5}
        >
          {isDraft && <KibTag>{t('Common:DRAFT')}</KibTag>}
          <Dotdotdot clamp={1}>
            <HtmlContentRenderer
              className={styles.messagePreview}
              content={messagePreview}
            />
          </Dotdotdot>
        </Stack>
      </Grid>
    </TableCell>
  )
}

export default SubjectCell
